import { useEffect, useState } from "react";
import { fetchSeatPreview } from "Services/CommonServices";
import { AddOnInvoicePreview } from "types/perSeat";

export interface SeatPreviewState {
  preview: AddOnInvoicePreview | null;
  isLoading: boolean;
  /** The preview call failed; the action can still proceed. */
  hasFailed: boolean;
}

/** Wait this long after the last count change before calling the API. */
const DEBOUNCE_MS = 350;

/**
 * Cost of changing to `targetSeats` total seats, fetched while `enabled` is
 * true. Refetches (debounced) whenever `targetSeats` changes, so a stepper
 * held down or a fast typed value settles to one call, not one per click.
 *
 * Only the "manage seats" flow needs this — inviting or removing a user costs
 * nothing under the buy-upfront model. The endpoint is a preview, so nothing is
 * charged by calling it, and a failure is never fatal: the real charge is
 * confirmed server-side and a declined card comes back as a `402` on the seat
 * change itself.
 */
export const useSeatPreview = (
  enabled: boolean,
  targetSeats: number,
): SeatPreviewState => {
  const [preview, setPreview] = useState<AddOnInvoicePreview | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasFailed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      setPreview(null);
      setFailed(false);
      // A fetch can still be in flight from before `enabled` flipped false —
      // its own `finally` skips `setLoading(false)` once cancelled (below), so
      // without this, isLoading would be stuck true forever, permanently
      // disabling the confirm button on an unrelated later render.
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Shown for the whole debounce-plus-fetch window, not just the fetch, so
    // the panel never flashes a preview priced for the count the customer has
    // already moved past.
    setLoading(true);
    setFailed(false);
    const timer = setTimeout(async () => {
      try {
        const response = await fetchSeatPreview(targetSeats);
        if (!cancelled) setPreview(response);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, targetSeats]);

  return { preview, isLoading, hasFailed };
};

export default useSeatPreview;
